import { parseQuotation, parseRfq, type QuotationTotals, quotationTotals } from '@felix/domain';
import { type NumberValue, numberOf } from './requisitions.js';
import type {
  QuotationInput,
  RequisitionDetail,
  RfqDetail,
  RfqInput,
  RfqQuotation,
  RfqStatus,
} from './types.js';

/**
 * RFQ ve Supplier Comparison ekranlarının saf hesapları (M12b Task 14): yol, RFQ açma formu ve
 * teklif formu (gemi ve bulutla aynı şema; `@felix/domain`) ve canlı toplam.
 */

export const RFQ_PATH = '/purchasing/rfq';

/** `lines`: satır bazında karşılaştırma (ADR 0111 §C); `award`: bölme ve tur (ADR 0111 §D). */
export const RFQ_TABS = [
  'details',
  'quotations',
  'comparison',
  'lines',
  'award',
  'comments',
] as const;
export type RfqTab = (typeof RFQ_TABS)[number];

export type RfqRoute = { view: 'list' } | { view: 'detail'; id: string; tab: RfqTab };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Ekranın görünümü sorgu dizesinden (`?id=…`, `?id=…&tab=comparison`). */
export function rfqRoute(search: string): RfqRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (!id || !UUID.test(id)) return { view: 'list' };
  const tab = params.get('tab');
  return {
    view: 'detail',
    id,
    tab: (RFQ_TABS as readonly string[]).includes(tab ?? '') ? (tab as RfqTab) : 'details',
  };
}

export function rfqPath(route: RfqRoute): string {
  if (route.view === 'list') return RFQ_PATH;
  return route.tab === 'details'
    ? `${RFQ_PATH}?id=${route.id}`
    : `${RFQ_PATH}?id=${route.id}&tab=${route.tab}`;
}

/** RFQ durumunun rozeti: tasarımda "Sent to Suppliers" yeşil. */
export const RFQ_TONE: Record<RfqStatus, 'neutral' | 'success' | 'info'> = {
  draft: 'neutral',
  sent: 'success',
  selected: 'info',
  cancelled: 'neutral',
};

/** `YYYY-MM-DD` gününe gün ekler (UTC; saat dilimi etkisiz). */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/* ---------- RFQ açma formu ---------- */

export interface RfqFormValues {
  closingDate: string;
  currency: string;
  deliveryLocation: string;
  deliveryRequirement: string;
  paymentTerms: string;
  warranty: string;
  validityDays: number | '';
  technicalSpec: string;
  supplierIds: string[];
  /** Teklif istenecek talep satırları. */
  lineIds: string[];
}

/** Teklif kapanışı: varsayılan üç gün (tedarikçiye makul yanıt süresi). */
export const DEFAULT_CLOSING_DAYS = 3;
/** Teklif geçerliliği: tasarımdaki "30 days". */
export const DEFAULT_VALIDITY_DAYS = 30;

/**
 * Teklif istenecek satırlar (ADR 0111 §A): ofis revizyonu varsa onaylı listeden (onaylanan
 * miktar, kaldırılan yok, ofisin eklediği kalem var); yoksa talep satırları. Anahtar sunucudaki
 * `requisitionLineIds` değeridir.
 */
export function rfqLineChoices(
  requisition: RequisitionDetail,
): { key: string; lineNo: number | null; name: string; quantity: number; unit: string }[] {
  if (requisition.revision && requisition.approvedList) {
    return requisition.approvedList
      .filter((line) => line.change !== 'removed')
      .map((line) => ({
        key: line.key,
        lineNo: line.lineNo,
        name: line.name,
        quantity: line.approved,
        unit: line.unit,
      }));
  }
  return requisition.lines.map((line) => ({
    key: line.id,
    lineNo: line.lineNo,
    name: line.name,
    quantity: line.quantity,
    unit: line.unit,
  }));
}

export function rfqFormDefaults(requisition: RequisitionDetail, today: string): RfqFormValues {
  return {
    closingDate: addDays(today, DEFAULT_CLOSING_DAYS),
    currency: requisition.currency,
    deliveryLocation: requisition.port ?? '',
    deliveryRequirement: '',
    paymentTerms: '',
    warranty: '',
    validityDays: DEFAULT_VALIDITY_DAYS,
    technicalSpec: '',
    supplierIds: [],
    lineIds: rfqLineChoices(requisition).map((line) => line.key),
  };
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

export function toRfqInput(values: RfqFormValues, requisition: RequisitionDetail): RfqInput {
  const all = rfqLineChoices(requisition).every((line) => values.lineIds.includes(line.key));
  return {
    requisitionId: requisition.id,
    closingDate: values.closingDate,
    currency: values.currency.trim(),
    deliveryLocation: blank(values.deliveryLocation),
    deliveryRequirement: blank(values.deliveryRequirement),
    paymentTerms: blank(values.paymentTerms),
    warranty: blank(values.warranty),
    validityDays: values.validityDays === '' ? null : values.validityDays,
    technicalSpec: blank(values.technicalSpec),
    supplierIds: values.supplierIds,
    ...(all ? {} : { requisitionLineIds: values.lineIds }),
  };
}

/** Alan hataları (alan → kod); boşsa geçerli. */
export function validateRfqForm(values: RfqFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  const parsed = parseRfq({
    requisitionId: '00000000-0000-7000-8000-000000000000',
    closingDate: values.closingDate,
    currency: values.currency.trim(),
    deliveryLocation: blank(values.deliveryLocation),
    deliveryRequirement: blank(values.deliveryRequirement),
    paymentTerms: blank(values.paymentTerms),
    warranty: blank(values.warranty),
    validityDays: values.validityDays === '' ? null : values.validityDays,
    technicalSpec: blank(values.technicalSpec),
    supplierIds: values.supplierIds,
  });
  if (!parsed.ok) {
    for (const issue of parsed.issues) errors[issue.path.split('.')[0] as string] ??= issue.code;
  }
  if (values.lineIds.length === 0) errors.lineIds = 'NO_LINES';
  return errors;
}

/* ---------- Teklif formu ---------- */

/** Sayı girişinin form değeri olduğu gibi (ara metin dahil); sayıya `numberOf` ile çevrilir. */
type Money = NumberValue;

export interface QuotationFormValues {
  supplierId: string;
  rowVersion?: number;
  quotationRef: string;
  receivedDate: string;
  currency: string;
  leadTimeDays: Money;
  validityDays: Money;
  warranty: string;
  discount: Money;
  freight: Money;
  customs: Money;
  tax: Money;
  serviceFee: Money;
  remarks: string;
  /** RFQ kalemi → birim fiyat; boş, fiyatlanmadı. */
  prices: Record<string, Money>;
  /** RFQ kalemi → ücretsiz / promosyon (fiyat 0; ADR 0166 §1). */
  free: Record<string, boolean>;
}

export function emptyQuotationForm(
  rfq: RfqDetail,
  supplierId: string,
  today: string,
): QuotationFormValues {
  return {
    supplierId,
    quotationRef: '',
    receivedDate: today,
    currency: rfq.currency,
    leadTimeDays: '',
    validityDays: rfq.validityDays ?? '',
    warranty: '',
    discount: '',
    freight: '',
    customs: '',
    tax: '',
    serviceFee: '',
    remarks: '',
    prices: Object.fromEntries(rfq.lines.map((line) => [line.id, ''])),
    free: Object.fromEntries(rfq.lines.map((line) => [line.id, false])),
  };
}

export function quotationFormFrom(quotation: RfqQuotation, rfq: RfqDetail): QuotationFormValues {
  const prices = new Map(quotation.lines.map((line) => [line.rfqLineId, line.unitPrice]));
  const free = new Set(quotation.lines.filter((l) => l.freeOfCharge).map((l) => l.rfqLineId));
  return {
    supplierId: quotation.supplierId,
    rowVersion: quotation.rowVersion,
    quotationRef: quotation.quotationRef ?? '',
    receivedDate: quotation.receivedDate,
    currency: quotation.currency,
    leadTimeDays: quotation.leadTimeDays ?? '',
    validityDays: quotation.validityDays ?? '',
    warranty: quotation.warranty ?? '',
    discount: quotation.discount || '',
    freight: quotation.freight || '',
    customs: quotation.customs || '',
    tax: quotation.tax || '',
    serviceFee: quotation.serviceFee || '',
    remarks: quotation.remarks ?? '',
    prices: Object.fromEntries(rfq.lines.map((line) => [line.id, prices.get(line.id) ?? ''])),
    free: Object.fromEntries(rfq.lines.map((line) => [line.id, free.has(line.id)])),
  };
}

const amount = (value: Money) => {
  const number = numberOf(value);
  return number === '' ? 0 : number;
};
const optional = (value: Money) => {
  const number = numberOf(value);
  return number === '' ? null : number;
};

export function toQuotationInput(values: QuotationFormValues, rfq: RfqDetail): QuotationInput {
  return {
    quotationRef: blank(values.quotationRef),
    receivedDate: values.receivedDate,
    currency: values.currency.trim(),
    leadTimeDays: optional(values.leadTimeDays),
    validityDays: optional(values.validityDays),
    warranty: blank(values.warranty),
    discount: amount(values.discount),
    freight: amount(values.freight),
    customs: amount(values.customs),
    tax: amount(values.tax),
    serviceFee: amount(values.serviceFee),
    remarks: blank(values.remarks),
    lines: rfq.lines.map((line) => ({
      rfqLineId: line.id,
      unitPrice: optional(values.prices[line.id] ?? ''),
      // İşaret yalnız işaretli satırda gider: eski sunucu alanı tanımasa da gövde aynı kalır
      ...(values.free?.[line.id] ? { freeOfCharge: true } : {}),
    })),
    ...(values.rowVersion ? { rowVersion: values.rowVersion } : {}),
  };
}

/** Canlı toplam (`satin-alma-modulu.md` §6 "Total Landed Cost"); sunucuyla aynı hesap. */
export function quotationFormTotals(values: QuotationFormValues, rfq: RfqDetail): QuotationTotals {
  const input = toQuotationInput(values, rfq);
  return quotationTotals(input, rfq.lines);
}

export function validateQuotationForm(
  values: QuotationFormValues,
  rfq: RfqDetail,
): Record<string, string> {
  const { rowVersion: _rowVersion, ...input } = toQuotationInput(values, rfq);
  const parsed = parseQuotation({ ...input, supplierId: values.supplierId });
  const errors: Record<string, string> = {};
  if (!parsed.ok) {
    for (const issue of parsed.issues) {
      const [head, index] = issue.path.split('.');
      const key =
        head === 'lines' && index !== undefined
          ? `price:${rfq.lines[Number(index)]?.id ?? ''}`
          : (head as string);
      errors[key] ??= issue.code;
    }
    return errors;
  }
  // İndirim fiyatlanan kalemleri aşamaz (sunucu da reddeder): negatif teslim maliyeti olmaz
  if (parsed.value.discount > quotationFormTotals(values, rfq).itemsTotal) {
    errors.discount = 'EXCEEDS_ITEMS';
  }
  return errors;
}

/* ---------- Tedarikçi portalına yayın (ADR 0111 §C) ---------- */

export interface PublishFormValues {
  visibility: 'open' | 'invited';
  /** Tarayıcının yerel günü, `YYYY-MM-DD`. */
  date: string;
  /** Tarayıcının yerel saati, `HH:MM`. */
  time: string;
}

const pad = (value: number) => String(value).padStart(2, '0');

/** Varsayılan: açık RFQ değil, davetliler; son tarih üç gün sonra 17:00 (yerel). */
export function publishFormDefaults(now: Date): PublishFormValues {
  const date = new Date(now.getTime());
  date.setDate(date.getDate() + DEFAULT_CLOSING_DAYS);
  return {
    visibility: 'invited',
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: '17:00',
  };
}

/** Yerel gün ve saat → UTC ISO-8601 (kayıtlar UTC; CLAUDE.md "Zaman"); geçersizse `null`. */
export function localToUtcIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const local = new Date(`${date}T${time}:00`);
  return Number.isNaN(local.getTime()) ? null : local.toISOString();
}

/** Yayın formu: gün ve saat geçerli, son tarih gelecekte (sunucu da denetler). */
export function validatePublishForm(values: PublishFormValues, now: Date): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.date) errors.date = 'REQUIRED';
  if (!values.time) errors.time = 'REQUIRED';
  if (errors.date || errors.time) return errors;
  const closingAt = localToUtcIso(values.date, values.time);
  if (closingAt === null) errors.date = 'INVALID_DATE';
  else if (Date.parse(closingAt) <= now.getTime()) errors.date = 'CLOSING_IN_PAST';
  return errors;
}

/** Kalan süre (son tarihe): gün ve saat; geçtiyse `null`. */
export function timeLeft(closingAt: string, now: Date): { days: number; hours: number } | null {
  const ms = Date.parse(closingAt) - now.getTime();
  if (!(ms > 0)) return null;
  const hours = Math.floor(ms / 3_600_000);
  return { days: Math.floor(hours / 24), hours: hours % 24 };
}

/** İçerik hash'inin kısaltması (ekranda; tamamı başlıkta). */
export function shortHash(hash: string): string {
  return hash.length > 12 ? `${hash.slice(0, 8)}…${hash.slice(-4)}` : hash;
}
